import { CLASS_NAMES, EVENTS } from '@bs/constants';
import { MessageBus } from '@bs/utils';

/**
 * A class for the VideoLightbox module
 */
export default class VideoLightbox extends MessageBus {
  /**
   * Constructor function of the VideoLightbox class.
   * @param {HTMLElement} element - REQUIRED - the module's container
   */
  constructor(element) {
    super();
    this.element = element;
    this.init();
  }

  /**
   * Initializes the view by calling the functions to
   * create DOM references, setup event handlers, and
   * then create the event listeners
   * @returns {object} VideoLightbox A reference to the current instance of the class
   * @chainable
   */
  init() {
    this.cacheDomReferences().setupHandlers().enable();
    return this;
  }

  /**
   * Cache DOM References
   * @returns {object} VideoLightbox A reference to the current instance of the class
   * @chainable
   */
  cacheDomReferences() {
    this.videoLinks = this.element.querySelectorAll(
      `.${CLASS_NAMES.VIDEO_OPEN_LIGHTBOX_JS}`
    );
    return this;
  }

  /**
   * Create event handlers to enable interaction with view
   * @returns {object} VideoLightbox A reference to the current instance of the class
   * @chainable
   */
  enable() {
    this.videoLinks.forEach((link) => {
      link.addEventListener(EVENTS.CLICK, this.currentCardEvent.bind(this));
    });
    return this;
  }

  /**
   * Bind event handlers with the proper context of `this`.
   * @returns {object} A reference to the current instance of the class
   * @chainable
   */
  setupHandlers() {
    this.currentCardEvent = this.cardClickEvent.bind(this);
    window.addEventListener(EVENTS.SCROLL, this.onScroll.bind(this));
    return this;
  }

  /**
   * Scroll event handler
   */
  onScroll() {
    const { top } = this.element.getBoundingClientRect();
    const inViewport = top + 300 <= window.innerHeight;

    if (inViewport && this.element.classList.contains('dark')) {
      this.element.classList.add(CLASS_NAMES.IN_VIEWPORT);
    }
  }

  /**
   * Handle the click event for video links
   * @param {Event} ev - The click event
   */
  cardClickEvent(ev) {
    ev.preventDefault();
    const ytId = ev.currentTarget.parentElement.getAttribute('data-youtube-id');
    this.showLightbox(ytId);
  }

  /**
   * Show the lightbox with the video
   * @param {string} ytId - YouTube video ID
   * @returns {VideoLightbox} A reference to the current instance of the class
   */
  showLightbox(ytId) {
    const lightbox = document.createElement('div');
    lightbox.className = `${CLASS_NAMES.VIDEO_LIGHTBOX}`;

    lightbox.style.position = 'fixed';
    lightbox.style.top = '0';
    lightbox.style.left = '0';
    lightbox.style.width = '100%';
    lightbox.style.height = '100%';
    lightbox.style.backgroundColor = 'rgba(0, 0, 0, 0.8)';
    lightbox.style.display = 'flex';
    lightbox.style.justifyContent = 'center';
    lightbox.style.alignItems = 'center';
    lightbox.style.zIndex = '1000';

    const wrapper = document.createElement('div');
    wrapper.className = `${CLASS_NAMES.VIDEO_WRAPPER}`;
    wrapper.style.position = 'relative';
    wrapper.style.maxWidth = '95%';
    wrapper.style.maxHeight = '95%';
    wrapper.style.transition = 'opacity 400ms ease, transform 400ms ease';

    const iframe = document.createElement('iframe');
    iframe.src = `https://www.youtube.com/embed/${ytId}?autoplay=1`;
    iframe.frameBorder = '0';
    iframe.allow = `
      accelerometer; 
      autoplay; 
      clipboard-write; 
      encrypted-media; 
      gyroscope; 
      picture-in-picture
    `;
    iframe.allowFullscreen = true;

    iframe.style.width = '75vw';
    iframe.style.height = '42vw';
    iframe.style.opacity = '1';
    iframe.style.transform = 'scale(1)';
    iframe.style.transition = 'opacity 400ms ease, transform 400ms ease';

    wrapper.appendChild(iframe);
    lightbox.appendChild(wrapper);
    document.body.appendChild(lightbox);

    lightbox.addEventListener(EVENTS.CLICK, () => {
      wrapper.style.opacity = '0';
      wrapper.style.transform = 'scale(0.9)';

      setTimeout(() => {
        if (document.body.contains(lightbox)) {
          document.body.removeChild(lightbox);
        }
      }, 400);
    });

    return this;
  }
}
